/**
 * `store-api`'s doors (D165, platform.md §3–§4) — buying a game, the pass,
 * and the two questions `play-api` asks it.
 *
 * @remarks
 * - `POST /checkout` — {@link checkoutRequestSchema} in, with the identity
 *   cookie; {@link checkoutAnswerSchema} out: the Stripe Checkout page to
 *   send the browser to. The player must be signed in — an entitlement is
 *   an account's, not a browser's.
 * - `POST /webhooks/stripe` — Stripe's, signed; not a browser door.
 * - `GET /entitlements?gameId=&userId=` — {@link entitlementAnswerSchema}
 *   out. Internal: answered only at {@link STORE_BINDING_ORIGIN}, over a
 *   service binding, never from a browser (D259).
 *   An owner of the platform (D258) holds every game, as `owner`.
 * - `POST /creators/onboard` (D165b) — {@link onboardRequestSchema} in, with
 *   the identity cookie; {@link onboardAnswerSchema} out: Stripe's own
 *   onboarding page for the creator's Express account, made on first ask.
 * - `GET /creators/me` (D165b) — {@link creatorAnswerSchema} out: whether
 *   this account is a creator, and whether it can be paid.
 * - `GET /pass` (D165c) — {@link passDescriptionSchema} out: what the pass
 *   costs, read off Stripe's own price so the button and the charge cannot
 *   disagree, and (D167) which games it covers; 404 `pass_not_offered` on a
 *   deployment with no pass, or one whose catalog is not yet an offer.
 * - `POST /pass/checkout` (D165c) — {@link passCheckoutRequestSchema} in,
 *   with the identity cookie; {@link checkoutAnswerSchema} out: Stripe
 *   Checkout in subscription mode.
 * - `POST /engagements` (D165c) — {@link engagementRequestSchema} in from
 *   `play-api` over the binding; {@link engagementAnswerSchema} out: whether
 *   the session counted toward the month's pool (platform.md §3.2).
 * - `GET /pass/me` (D166) — {@link passHoldingSchema} out, with the identity
 *   cookie: the signed-in account's own pass — held, ending, lapsed — or
 *   `null` for an account that never had one.
 * - `POST /pass/portal` (D166) — {@link passPortalRequestSchema} in, with the
 *   identity cookie; {@link checkoutAnswerSchema} out: Stripe's Customer
 *   Portal for the account — cancel, resume, change the card, read the
 *   invoices. A pass sold with no way to cancel is not a pass anyone
 *   should buy.
 * - `GET /creators/me/statement?month=YYYY-MM` (D168) —
 *   {@link creatorStatementSchema} out, with the identity cookie: the
 *   creator's share of that month's pass pool, with every number it was
 *   computed from and the engaged sessions per game — the published
 *   formula, shown. `null` for a month the run has not written.
 *
 * @packageDocumentation
 */

import { gameIdSchema } from '@orpheus/content-schema';
import { z } from 'zod';

/**
 * Where `play-api` calls the store over its service binding (D259). No
 * request from the internet can carry this host: Cloudflare routes the
 * internet to the store only at its custom domain. So the store's internal
 * doors answer this origin and nothing else. A header once did that job,
 * and anyone could send a header.
 */
export const STORE_BINDING_ORIGIN = 'https://store';

export const STORE_CHECKOUT_PATH = '/checkout';
export const STORE_WEBHOOK_PATH = '/webhooks/stripe';
export const STORE_ENTITLEMENT_PATH = '/entitlements';
export const STORE_ONBOARD_PATH = '/creators/onboard';
export const STORE_CREATOR_PATH = '/creators/me';
export const STORE_PASS_PATH = '/pass';
export const STORE_PASS_CHECKOUT_PATH = '/pass/checkout';
export const STORE_ENGAGEMENT_PATH = '/engagements';
export const STORE_PASS_HOLDING_PATH = '/pass/me';
export const STORE_PASS_PORTAL_PATH = '/pass/portal';
export const STORE_STATEMENT_PATH = '/creators/me/statement';

/** Buy this game, and come back here — the page the player was on, which the Worker checks against its allowed origins. */
export const checkoutRequestSchema = z
	.strictObject({
		gameId: gameIdSchema,
		returnUrl: z.url(),
	})
	.readonly();

export type CheckoutRequest = z.infer<typeof checkoutRequestSchema>;

/** Where to send the browser. */
export const checkoutAnswerSchema = z.strictObject({ url: z.url() }).readonly();

export type CheckoutAnswer = z.infer<typeof checkoutAnswerSchema>;

/** Under which model a player holds a game (platform.md §3.2's two rows). */
export const entitlementModelSchema = z.enum(['pass', 'purchase']);

export type EntitlementModel = z.infer<typeof entitlementModelSchema>;

/**
 * Why a player holds a game: under one of the two models a grant records,
 * or (D258) because the account owns the platform — which no row says, so
 * it is an answer and never a model a grant can carry.
 */
const holdingModelSchema = z.enum([...entitlementModelSchema.options, 'owner']);

/** Whether this player may stand in this game's paid rooms, and why. */
export const entitlementAnswerSchema = z
	.strictObject({
		isEntitled: z.boolean(),
		model: holdingModelSchema.optional(),
	})
	.readonly();

export type EntitlementAnswer = z.infer<typeof entitlementAnswerSchema>;

/** A door that only needs to know where to send the browser back to. */
const returnToSchema = z
	.strictObject({
		returnUrl: z.url(),
	})
	.readonly();

/** Become a creator, or resume becoming one: the page to come back to when Stripe is done, or when its link expires. */
export const onboardRequestSchema = returnToSchema;

export type OnboardRequest = z.infer<typeof onboardRequestSchema>;

/** Where to send the browser: Stripe's hosted onboarding, one-time and short-lived. */
export const onboardAnswerSchema = z.strictObject({ url: z.url() }).readonly();

export type OnboardAnswer = z.infer<typeof onboardAnswerSchema>;

/**
 * A creator, as the store knows them (D165b): their connected account, and
 * whether Stripe will pay it — `isPayable` is `charges_enabled` AND
 * `payouts_enabled`, stamped by the `account.updated` webhook, and the
 * checkout refuses a game paid into an account that is not.
 */
export const creatorAnswerSchema = z
	.strictObject({
		isPayable: z.boolean(),
		stripeAccountId: z.string().regex(/^acct_[A-Za-z0-9]+$/u),
	})
	.readonly()
	.nullable();

export type CreatorAnswer = z.infer<typeof creatorAnswerSchema>;

/**
 * What the pass costs (D165c, platform.md §3.2): the list price, and the
 * introductory price when the deployment offers one — both read off
 * Stripe's own Price and Coupon, never typed twice. The intro is applied
 * only to an account's FIRST pass (Mike, 2026-09-10): a lapsed subscriber
 * pays list, and the store enforces that rather than the coupon.
 */
export const passDescriptionSchema = z
	.strictObject({
		currency: z.string().regex(/^[a-z]{3}$/u),
		/** The games the pass covers (D167), by id and title — never fewer than the store's minimum, or the pass is not offered. */
		games: z.array(z.strictObject({ gameId: z.string().min(1), title: z.string().min(1) }).readonly()).readonly(),
		intro: z
			.strictObject({
				months: z.int().positive(),
				priceCents: z.int().nonnegative(),
			})
			.readonly()
			.optional(),
		priceCents: z.int().nonnegative(),
	})
	.readonly();

export type PassDescription = z.infer<typeof passDescriptionSchema>;

/** Subscribe to the pass, and come back here. */
export const passCheckoutRequestSchema = returnToSchema;

export type PassCheckoutRequest = z.infer<typeof passCheckoutRequestSchema>;

/**
 * The signed-in account's own pass (D166), as Stripe last said it: the raw
 * status (Stripe's word, mirrored — a new one tomorrow is stored, not
 * refused), whether that status holds the pass, when the paid period ends,
 * and whether the account has asked for it to end there. `null` for an
 * account that has never held one.
 */
export const passHoldingSchema = z
	.strictObject({
		/**
		 * When the introductory price ends (D167) — Stripe's `discount.end` — so a holder can be told the price is about to
		 * change; absent when there is no intro, or it has no end Stripe named.
		 */
		introEndsAtIso: z.iso.datetime().optional(),
		/** The account asked to cancel; the pass is held until `periodEndsAtIso` and not renewed. */
		isCancelingAtPeriodEnd: z.boolean(),
		/** Whether `status` holds the pass — `active` or `trialing`. */
		isHeld: z.boolean(),
		/** When the paid period ends; absent when Stripe's event did not carry it. */
		periodEndsAtIso: z.iso.datetime().optional(),
		status: z.string().min(1),
	})
	.readonly()
	.nullable();

export type PassHolding = z.infer<typeof passHoldingSchema>;

/** Open Stripe's Customer Portal for the account, and come back here. */
export const passPortalRequestSchema = returnToSchema;

export type PassPortalRequest = z.infer<typeof passPortalRequestSchema>;

/**
 * An engaged session (D165c, platform.md §3.2): `play-api` reports that a
 * signed-in player's session on a pass game passed the turn threshold. The
 * store counts it once per player, per game, per day — the day is the
 * store's clock, so two Workers cannot disagree about midnight.
 */
export const engagementRequestSchema = z
	.strictObject({
		gameId: gameIdSchema,
		sessionId: z.string().min(1),
		userId: z.string().min(1),
	})
	.readonly();

export type EngagementRequest = z.infer<typeof engagementRequestSchema>;

/** Whether the session counted — false when the day already has one, or the game is not in the pass. */
export const engagementAnswerSchema = z.strictObject({ isCounted: z.boolean() }).readonly();

export type EngagementAnswer = z.infer<typeof engagementAnswerSchema>;

/** A month as the ledger names it: `YYYY-MM`. */
export const ledgerMonthSchema = z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/u);

/** What a payout row is, in the ledger's own words (platform.md §3.2). */
export const payoutStatusSchema = z.enum(['dry_run', 'due', 'kept', 'paid']);

export type PayoutStatus = z.infer<typeof payoutStatusSchema>;

/**
 * A creator's statement for one month (D168): the platform's numbers
 * (revenue, fees, cut, pool, every engaged session), the creator's (their
 * sessions, their share, whether and how it was paid), and their sessions
 * per game — every number the published formula needs, so the statement
 * can be checked by reading it. `null` when the month's run has not been
 * written yet.
 */
export const creatorStatementSchema = z
	.strictObject({
		/** The creator's share, in minor units. */
		amountCents: z.int().nonnegative(),
		currency: z.string().regex(/^[a-z]{3}$/u),
		/** Our cut of the revenue (platform.md §3.1). */
		cutCents: z.int().nonnegative(),
		/** The creator's engaged sessions, on every game of theirs the pass covered. */
		engagedSessions: z.int().nonnegative(),
		/** What Stripe took on the month's invoices. */
		feeCents: z.int().nonnegative(),
		/** Those sessions, per game. */
		games: z.array(z.strictObject({ engagedSessions: z.int().positive(), gameId: z.string().min(1) }).readonly()).readonly(),
		month: ledgerMonthSchema,
		/** revenue − fees − cut: what was divided. */
		poolCents: z.int().nonnegative(),
		/** The month's pass invoices, less those refunded. */
		revenueCents: z.int().nonnegative(),
		status: payoutStatusSchema,
		/** The transfer that paid it, once `paid`. */
		stripeTransferId: z.string().min(1).optional(),
		/** Every engaged session on every pass game that month — the divisor. */
		totalSessions: z.int().nonnegative(),
	})
	.readonly()
	.nullable();

export type CreatorStatement = z.infer<typeof creatorStatementSchema>;

/** Every name a store door can refuse with. */
export const STORE_API_ERRORS = [
	/** The webhook's signature did not verify — Stripe's secret and ours disagree, or it is not Stripe. */
	'bad_signature',
	/** The game is paid into a connected account Stripe will not pay yet — its creator has not finished onboarding. */
	'creator_not_payable',
	'game_not_for_sale',
	'game_not_found',
	'not_a_checkout_request',
	/** The signed-in account is not a creator — no connected account, so no statement (D168). */
	'not_a_creator',
	/** `?month=` is not `YYYY-MM` (D168). */
	'not_a_month',
	/** D241: a Record door's body is not its shape. */
	'not_a_record_request',
	'not_an_engagement',
	'not_an_onboard_request',
	'not_found',
	'not_json',
	/** An entitlement belongs to an account; sign in first. */
	'not_signed_in',
	/** The account already holds the pass — Stripe would make a second subscription, so the store does not ask it to. */
	'pass_already_held',
	/** This deployment offers no pass (no price configured). */
	'pass_not_offered',
	/** The return page is not one of ours. */
	'return_url_not_allowed',
	/** Stripe refused or could not be reached. */
	'stripe_unavailable',
] as const;

export type StoreApiErrorName = (typeof STORE_API_ERRORS)[number];

/**
 * What `POST /creators/onboard` can answer with (Calder, D165b): the
 * door's own refusals, plus the router's `not_found` for a deployed store
 * that predates the door. A copy table total over this list is asked by
 * the compiler about every new refusal the door learns — the shape of the
 * walker fix (D165a §5), applied to words.
 */
export const ONBOARD_REFUSALS = ['not_an_onboard_request', 'not_found', 'not_json', 'not_signed_in', 'return_url_not_allowed', 'stripe_unavailable'] as const satisfies readonly StoreApiErrorName[];

export type OnboardRefusal = (typeof ONBOARD_REFUSALS)[number];

/** What `POST /pass/checkout` can answer with (D165c), on the same terms. */
export const PASS_CHECKOUT_REFUSALS = [
	'not_a_checkout_request',
	'not_found',
	'not_json',
	'not_signed_in',
	'pass_already_held',
	'pass_not_offered',
	'return_url_not_allowed',
	'stripe_unavailable',
] as const satisfies readonly StoreApiErrorName[];

export type PassCheckoutRefusal = (typeof PASS_CHECKOUT_REFUSALS)[number];

/**
 * What `POST /pass/portal` can answer with (D166), on the same terms. `not_found` is the door's own here too: an account
 * that never held the pass has no customer to open a portal for — reachable only by a race, since the button is offered
 * to accounts with a pass row.
 */
export const PASS_PORTAL_REFUSALS = ['not_a_checkout_request', 'not_found', 'not_json', 'not_signed_in', 'return_url_not_allowed', 'stripe_unavailable'] as const satisfies readonly StoreApiErrorName[];

export type PassPortalRefusal = (typeof PASS_PORTAL_REFUSALS)[number];

/** What `GET /creators/me/statement` can answer with (D168), on the same terms. */
export const STATEMENT_REFUSALS = ['not_a_creator', 'not_a_month', 'not_found', 'not_signed_in'] as const satisfies readonly StoreApiErrorName[];

export type StatementRefusal = (typeof STATEMENT_REFUSALS)[number];

/** Why a store door refused, by name. */
export const storeApiErrorSchema = z
	.strictObject({
		error: z.enum(STORE_API_ERRORS),
	})
	.readonly();

export type StoreApiError = z.infer<typeof storeApiErrorSchema>;
